import * as React from "react";
import { InputConnector, InputConnectorsApi, Notification } from "../../client";
import { ITableProps, useApiModels } from "../../shared/hooks/useApiModels";
import { Table } from "antd";
import moment from "moment/moment";
import { DATE_TIME_FORMAT } from "../../shared/constants";
import { Link } from "react-router-dom";
import { useMemo } from "react";

interface IProps {
    notifications: Notification[];
    loading: boolean;
    tableProps: ITableProps<Notification>;
}

export const NotificationTable: React.FC<IProps> = (props) => {
    const inputConnectorIds: string[] = useMemo(() => {
        return Array.from(
            new Set(
                props.notifications.flatMap((notification) =>
                    notification.input_connector_id
                        ? [String(notification.input_connector_id)]
                        : []
                )
            )
        );
    }, [props.notifications]);

    const { models: inputConnectors } = useApiModels<InputConnector>(
        (page, filters) =>
            new InputConnectorsApi().listInputConnectors({
                page,
                ids: inputConnectorIds.join(",") || "0",
                ...filters,
            }),
        "generic",
        [props.notifications]
    );

    const inputConnectorsById: { [key: number]: InputConnector } =
        useMemo(() => {
            return inputConnectors.reduce(
                (prev, inputConnector) => ({
                    ...prev,
                    [inputConnector.id]: inputConnector,
                }),
                {}
            );
        }, [inputConnectors]);

    return (
        <Table<Notification>
            dataSource={props.notifications}
            loading={props.loading}
            rowKey="id"
            title={() => "Notifications"}
            {...props.tableProps}
            columns={[
                {
                    key: "created",
                    dataIndex: "created",
                    title: "Created",
                    render: (created) =>
                        moment(created).format(DATE_TIME_FORMAT),
                },
                {
                    key: "type",
                    dataIndex: "type",
                    title: "Type",
                    filterMultiple: false,
                    filters: [
                        {
                            text: "Error",
                            value: "error",
                        },
                        {
                            text: "Info",
                            value: "info",
                        },
                    ],
                },
                {
                    key: "description",
                    dataIndex: "description",
                    title: "Description",
                },
                {
                    key: "input_connector_id",
                    dataIndex: "input_connector_id",
                    title: "Input connector",
                    width: "250px",
                    render: (inputConnectorId) =>
                        inputConnectorsById[inputConnectorId] ? (
                            <Link to={`/inputs/${inputConnectorId}`}>
                                {inputConnectorsById[inputConnectorId].name}
                            </Link>
                        ) : (
                            "-"
                        ),
                    filterMultiple: false,
                    filters: inputConnectors.map((inputConnector) => ({
                        text: inputConnector.name,
                        value: inputConnector.id,
                    })),
                },
                {
                    key: "input_record_id",
                    dataIndex: "input_record_id",
                    title: "Input record",
                    render: (inputRecordId) => (
                        <Link to={`/input-records/${inputRecordId}`}>
                            {inputRecordId}
                        </Link>
                    ),
                },
                {
                    key: "input_record_group_id",
                    dataIndex: "input_record_group_id",
                    title: "Input record group",
                    render: (inputRecordGroupId) => (
                        <Link to={`/groups/${inputRecordGroupId}`}>
                            {inputRecordGroupId}
                        </Link>
                    ),
                },
                {
                    key: "exception",
                    dataIndex: "exception",
                    title: "Exception",
                },
            ]}
        />
    );
};
